import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, Plus, Trash2, Pencil, Search, Globe, Loader2, AlertTriangle, Check, X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { API, PLATFORMS, getPlatformName } from "./constants";

const LABELS = {
  en: {
    title: "Account Management",
    desc: "Add, edit, or remove social media accounts being tracked",
    addAccount: "Add Account",
    editAccount: "Edit Account",
    deleteAccount: "Delete Account",
    platform: "Platform",
    username: "Username",
    displayName: "Display Name",
    group: "Group",
    profileUrl: "Profile URL",
    search: "Search accounts...",
    total: "Total",
    noAccounts: "No accounts found",
    confirmDelete: "Are you sure you want to delete this account?",
    deleteWarning: "This will also delete all posts and analysis data for this account.",
    keepData: "Keep posts data",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    followers: "Followers",
    posts: "Posts",
    lastSynced: "Last synced",
    allPlatforms: "All Platforms",
    allGroups: "All Groups",
    ungrouped: "Ungrouped",
    addNew: "Add New Account",
    addByUrl: "Add by URL",
    urlPlaceholder: "Enter account profile URL",
    adding: "Adding...",
    saving: "Saving...",
    optional: "optional",
  },
  ko: {
    title: "계정 관리",
    desc: "트래킹 중인 소셜 미디어 계정을 추가, 수정, 삭제합니다",
    addAccount: "계정 추가",
    editAccount: "계정 수정",
    deleteAccount: "계정 삭제",
    platform: "플랫폼",
    username: "사용자명",
    displayName: "표시 이름",
    group: "그룹",
    profileUrl: "프로필 URL",
    search: "계정 검색...",
    total: "전체",
    noAccounts: "계정이 없습니다",
    confirmDelete: "이 계정을 삭제하시겠습니까?",
    deleteWarning: "해당 계정의 모든 게시물 및 분석 데이터가 함께 삭제됩니다.",
    keepData: "게시물 데이터 유지",
    cancel: "취소",
    save: "저장",
    delete: "삭제",
    followers: "팔로워",
    posts: "게시물",
    lastSynced: "마지막 동기화",
    allPlatforms: "전체 플랫폼",
    allGroups: "전체 그룹",
    ungrouped: "미분류",
    addNew: "새 계정 추가",
    addByUrl: "URL로 추가",
    urlPlaceholder: "계정 프로필 URL을 입력하세요",
    adding: "추가 중...",
    saving: "저장 중...",
    optional: "선택사항",
  },
  zh: {
    title: "账户管理",
    desc: "添加、编辑或删除正在追踪的社交媒体账户",
    addAccount: "添加账户",
    editAccount: "编辑账户",
    deleteAccount: "删除账户",
    platform: "平台",
    username: "用户名",
    displayName: "显示名称",
    group: "分组",
    profileUrl: "个人主页URL",
    search: "搜索账户...",
    total: "总计",
    noAccounts: "没有找到账户",
    confirmDelete: "确定要删除此账户吗？",
    deleteWarning: "这将同时删除该账户的所有帖子和分析数据。",
    keepData: "保留帖子数据",
    cancel: "取消",
    save: "保存",
    delete: "删除",
    followers: "粉丝",
    posts: "帖子",
    lastSynced: "最后同步",
    allPlatforms: "全部平台",
    allGroups: "全部分组",
    ungrouped: "未分组",
    addNew: "添加新账户",
    addByUrl: "通过URL添加",
    urlPlaceholder: "输入账户个人主页URL",
    adding: "添加中...",
    saving: "保存中...",
    optional: "可选",
  },
};

const formatNum = (n) => {
  if (!n && n !== 0) return "-";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
};

const AccountManagement = ({ lang = "en" }) => {
  const t = LABELS[lang] || LABELS.en;
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPlatform, setFilterPlatform] = useState("all");
  const [filterGroup, setFilterGroup] = useState("all");

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ platform: "instagram", url: "", display_name: "", group: "" });
  const [adding, setAdding] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [editForm, setEditForm] = useState({ display_name: "", group: "", username: "" });
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [keepData, setKeepData] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/accounts`);
      setAccounts(res.data || []);
    } catch {
      toast.error("Failed to fetch accounts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  // Unique groups from accounts
  const groups = [...new Set(accounts.map(a => a.group || "").filter(Boolean))];

  // Filtered accounts
  const filtered = accounts.filter(a => {
    if (filterPlatform !== "all" && a.platform !== filterPlatform) return false;
    if (filterGroup !== "all") {
      if (filterGroup === "ungrouped" && (a.group || "").trim()) return false;
      if (filterGroup !== "ungrouped" && a.group !== filterGroup) return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (a.username || "").toLowerCase().includes(q) ||
        (a.display_name || "").toLowerCase().includes(q) ||
        (a.group || "").toLowerCase().includes(q);
    }
    return true;
  });

  // --- Add account ---
  const handleAdd = async () => {
    if (!addForm.url.trim()) { toast.error("URL is required"); return; }
    setAdding(true);
    try {
      const res = await axios.post(`${API}/sources/accounts`, {
        platform: addForm.platform,
        url: addForm.url.trim(),
        display_name: addForm.display_name.trim() || undefined,
        group: addForm.group.trim() || undefined,
      });
      if (res.data.success) {
        toast.success(`Account added: ${res.data.display_name || res.data.username}`);
        setAddOpen(false);
        setAddForm({ platform: "instagram", url: "", display_name: "", group: "" });
        fetchAccounts();
      } else {
        toast.error(res.data.message || "Failed to add account");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add account");
    } finally {
      setAdding(false);
    }
  };

  // --- Edit account ---
  const openEdit = (acc) => {
    setEditAccount(acc);
    setEditForm({ display_name: acc.display_name || "", group: acc.group || "", username: acc.username || "" });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editAccount) return;
    setSaving(true);
    try {
      await axios.put(`${API}/accounts/${editAccount.id}`, {
        display_name: editForm.display_name.trim() || undefined,
        group: editForm.group.trim() || undefined,
        username: editForm.username.trim() || undefined,
      });
      toast.success("Account updated");
      setEditOpen(false);
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  // --- Delete account ---
  const openDelete = (acc) => {
    setDeleteTarget(acc);
    setKeepData(false);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/accounts/${deleteTarget.id}?keep_data=${keepData}`);
      toast.success("Account deleted");
      setDeleteOpen(false);
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const getPlatformColor = (p) => {
    const found = PLATFORMS.find(pl => pl.id === p);
    return found?.color || "#6b7280";
  };

  return (
    <div className="space-y-4" data-testid="account-management">
      {/* Header */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t.title}
              </CardTitle>
              <CardDescription>{t.desc}</CardDescription>
            </div>
            <Button onClick={() => setAddOpen(true)} size="sm" data-testid="add-account-btn">
              <Plus className="h-4 w-4 mr-1" /> {t.addAccount}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t.search}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
                data-testid="account-search"
              />
            </div>
            <Select value={filterPlatform} onValueChange={setFilterPlatform}>
              <SelectTrigger className="w-[160px] h-9" data-testid="filter-platform">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.allPlatforms}</SelectItem>
                {PLATFORMS.map(p => (
                  <SelectItem key={p.id} value={p.id}>{getPlatformName(p.id, lang)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterGroup} onValueChange={setFilterGroup}>
              <SelectTrigger className="w-[160px] h-9" data-testid="filter-group">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.allGroups}</SelectItem>
                <SelectItem value="ungrouped">{t.ungrouped}</SelectItem>
                {groups.map(g => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Badge variant="secondary" className="shrink-0">
              {t.total}: {filtered.length}
            </Badge>
          </div>

          {/* Account List */}
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">{t.noAccounts}</div>
          ) : (
            <div className="space-y-2">
              {filtered.map(acc => (
                <div
                  key={acc.id}
                  className="flex flex-col gap-2 rounded-lg border border-border/40 p-3 hover:bg-muted/30 transition-colors sm:flex-row sm:items-center sm:justify-between"
                  data-testid={`account-row-${acc.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Avatar */}
                    <div className="shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: getPlatformColor(acc.platform) }}>
                      {(acc.display_name || acc.username || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">{acc.display_name || acc.username}</span>
                        <Badge variant="outline" className="text-[10px] h-5 shrink-0" style={{ borderColor: getPlatformColor(acc.platform), color: getPlatformColor(acc.platform) }}>
                          {getPlatformName(acc.platform, lang)}
                        </Badge>
                        {acc.group && (
                          <Badge variant="secondary" className="text-[10px] h-5 shrink-0">{acc.group}</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-3 mt-0.5">
                        <span>@{acc.username}</span>
                        <span>{t.followers}: {formatNum(acc.followers)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 sm:ml-auto">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(acc)} data-testid={`edit-account-${acc.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => openDelete(acc)} data-testid={`delete-account-${acc.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Account Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-account-dialog">
          <DialogHeader>
            <DialogTitle>{t.addNew}</DialogTitle>
            <DialogDescription>{t.addByUrl}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t.platform}</Label>
              <Select value={addForm.platform} onValueChange={v => setAddForm(f => ({ ...f, platform: v }))}>
                <SelectTrigger data-testid="add-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map(p => (
                    <SelectItem key={p.id} value={p.id}>{getPlatformName(p.id, lang)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t.profileUrl} *</Label>
              <Input
                value={addForm.url}
                onChange={e => setAddForm(f => ({ ...f, url: e.target.value }))}
                placeholder={t.urlPlaceholder}
                data-testid="add-url-input"
              />
            </div>
            <div>
              <Label>{t.displayName} ({t.optional})</Label>
              <Input
                value={addForm.display_name}
                onChange={e => setAddForm(f => ({ ...f, display_name: e.target.value }))}
                data-testid="add-display-name"
              />
            </div>
            <div>
              <Label>{t.group} ({t.optional})</Label>
              <Input
                value={addForm.group}
                onChange={e => setAddForm(f => ({ ...f, group: e.target.value }))}
                placeholder="e.g. A2O MAY"
                data-testid="add-group-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>{t.cancel}</Button>
            <Button onClick={handleAdd} disabled={adding} data-testid="add-account-submit">
              {adding ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />{t.adding}</> : <><Plus className="h-4 w-4 mr-1" />{t.addAccount}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Account Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-account-dialog">
          <DialogHeader>
            <DialogTitle>{t.editAccount}</DialogTitle>
            <DialogDescription>
              {editAccount?.platform} - @{editAccount?.username}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t.displayName}</Label>
              <Input
                value={editForm.display_name}
                onChange={e => setEditForm(f => ({ ...f, display_name: e.target.value }))}
                data-testid="edit-display-name"
              />
            </div>
            <div>
              <Label>{t.group}</Label>
              <Input
                value={editForm.group}
                onChange={e => setEditForm(f => ({ ...f, group: e.target.value }))}
                placeholder="e.g. A2O MAY"
                data-testid="edit-group-input"
              />
            </div>
            <div>
              <Label>{t.username}</Label>
              <Input
                value={editForm.username}
                onChange={e => setEditForm(f => ({ ...f, username: e.target.value }))}
                data-testid="edit-username"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>{t.cancel}</Button>
            <Button onClick={handleEdit} disabled={saving} data-testid="edit-account-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />{t.saving}</> : <><Check className="h-4 w-4 mr-1" />{t.save}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              {t.deleteAccount}
            </DialogTitle>
            <DialogDescription>{t.confirmDelete}</DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="rounded-lg border border-border/40 p-3">
              <div className="font-medium">{deleteTarget.display_name || deleteTarget.username}</div>
              <div className="text-sm text-muted-foreground">
                {getPlatformName(deleteTarget.platform, lang)} - @{deleteTarget.username}
              </div>
            </div>
          )}
          <div className="text-sm text-muted-foreground flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
            {t.deleteWarning}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="keep-data"
              checked={keepData}
              onChange={e => setKeepData(e.target.checked)}
              className="rounded"
            />
            <Label htmlFor="keep-data" className="text-sm cursor-pointer">{t.keepData}</Label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>{t.cancel}</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="delete-account-confirm">
              {deleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              {t.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountManagement;
